import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { useListContext } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/contexts/ListContext';
import type { EventsWrapperProps } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/EventsWrapper';
import type { ListLayoutsDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import Grid, {
  Col,
  getBpNum,
  Row,
} from 'hb-react/shared/components/DesignSystem/Grid';

import Calendar from './components/Calendar';
import Details from './components/Details';

const ListItem = ({
  dynamicAttributes,
  event,
  ids,
  isBackOffice,
  navigate,
  whiteLabelUrl,
}: Pick<
  EventsWrapperProps,
  'navigate' | 'isBackOffice' | 'ids' | 'whiteLabelUrl'
> & {
  dynamicAttributes?: ListLayoutsDynamicAttributes;
  event: Event;
}) => {
  const {
    setHoverEffectImage,
    hoverEffectImage,
    hoverEffectTitle,
    handleMouseEnterInTitle,
    handleMouseLeaveInTitle,
  } = useListContext();

  const {
    new_cover_picture: newCoverPicture,
    start_date_timestamp: startDateTimestamp,
    timezone,
    id,
  } = event;
  const { displayDate, displayImage } = dynamicAttributes?.fields || {};

  const handleMouseEnterInImage = () =>
    setHoverEffectImage({ cardId: id, isHover: true });

  const handleMouseLeaveInImage = () =>
    setHoverEffectImage({ cardId: null, isHover: false });

  return (
    <Grid
      columnsInMobile
      mx={0}
      numCols={{
        _: 6,
        m: 12,
        l: 12,
        xl: 10,
      }}
      width="100%"
      mb={{
        _: 6,
        l: 8,
      }}
    >
      <Row>
        {displayDate && (
          <Col width={({ numCols }) => 1 / getBpNum(numCols, 's')}>
            <Calendar timeStamp={startDateTimestamp} timezone={timezone} />
          </Col>
        )}

        {displayImage && (
          <Col
            width={({ numCols }) => ({
              s: 5 / getBpNum(numCols, 's'),
              m: 4 / getBpNum(numCols, 'm'),
              l: 4 / getBpNum(numCols, 'l'),
              xl: 3 / getBpNum(numCols, 'xl'),
            })}
          >
            <ImgWrapper>
              <Img
                alt={__('Cover picture')}
                $hoverOnTitle={hoverEffectTitle.isHover}
                $isRightCard={hoverEffectTitle.cardId === id}
                onClick={() =>
                  ids &&
                  navigate &&
                  goToModuleDetailPage({
                    event,
                    ids,
                    isBackOffice,
                    navigate,
                    whiteLabelUrl,
                  })
                }
                data-testid="builder-cover-wrapper"
                onMouseEnter={handleMouseEnterInImage}
                onMouseLeave={handleMouseLeaveInImage}
                src={newCoverPicture.medium_url}
                tabIndex={0}
              />
            </ImgWrapper>
          </Col>
        )}
        <Col
          offset={({ numCols }) => ({
            s: 1 / getBpNum(numCols, 's'),
            m: 0,
            l: 0,
          })}
          width={({ numCols }) => ({
            s: 5 / getBpNum(numCols, 's'),
            m: 7 / getBpNum(numCols, 'm'),
            xl: 6 / getBpNum(numCols, 'xl'),
          })}
          mt={-1}
        >
          <Box width="100%">
            <Details
              dynamicAttributes={dynamicAttributes}
              event={event}
              handleMouseEnterInTitle={handleMouseEnterInTitle}
              handleMouseLeaveInTitle={handleMouseLeaveInTitle}
              ids={ids}
              isBackOffice={isBackOffice}
              hoverEffectImage={hoverEffectImage}
              navigate={navigate}
              whiteLabelUrl={whiteLabelUrl}
            />
          </Box>
        </Col>
      </Row>
    </Grid>
  );
};

export default ListItem;

const Img = styled.img<{ $hoverOnTitle: boolean; $isRightCard: boolean }>`
  cursor: pointer;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  width: 100%;
  ${({ $hoverOnTitle, $isRightCard }) =>
    $hoverOnTitle &&
    $isRightCard &&
    css`
      transform: scale(1.1) !important;
    `}
  &:hover {
    transform: scale(1.1) !important;
  }
`;

const ImgWrapper = styled(Box).attrs(() => ({
  height: { _: '145px', m: '116px', l: '156px', xl: '168px' },
  overflow: 'hidden',
  width: { _: '283px', m: '227px', l: '304px', xl: '326px' },
}))`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;
